import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
    Box,
    Typography,
    IconButton,
    InputBase,
    Checkbox,
    Button,
    Divider,
    CircularProgress,
    Avatar,
    Menu,
    MenuItem,
    Link,
    Grid
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import Modal from 'components/common/Modal';
import { CloseOutlinedIcon, PersonOutlineOutlinedIcon, FilterAltOutlinedIcon } from 'icons/materialIcons';
import { useTeamMember } from 'team/contexts/teamMemberContext';
import TeamWhiteButtonWithIcon from 'team/shared/components/button/TeamWhiteButtonWithIcon';
import { getTeamMembers } from 'team/apis/teamMemberApi';
import { getTemplate, applyTemplateToMember } from 'team/apis/teamTemplateApi';
import { getTeamSubteams } from 'team/apis/teamSubteamApi';
import PropTypes from 'prop-types';
import avatarPlaceholder from 'images/avatar-cropped.png';

import { getSubteamColor, getSubteamInitials } from 'team/shared/utils/subteamUtils';

const TeamTemplateAssignMembersModal = ({ open, onClose, template, teamId, onSave }) => {
    const { getMembers } = useTeamMember();
    const navigate = useNavigate();
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedAssigned, setSelectedAssigned] = useState(new Set());
    const [selectedUnassigned, setSelectedUnassigned] = useState(new Set());
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [allTeamMembersList, setAllTeamMembersList] = useState([]);
    const [loadingMembers, setLoadingMembers] = useState(false);
    const [currentTemplate, setCurrentTemplate] = useState(template);
    const [subteamFilterAnchor, setSubteamFilterAnchor] = useState(null);
    const [subteams, setSubteams] = useState([]);
    const [selectedSubteams, setSelectedSubteams] = useState(new Set());
    const [appliedSubteamFilter, setAppliedSubteamFilter] = useState(new Set());
    const [loadingSubteams, setLoadingSubteams] = useState(false);
    const modalContentRef = useRef(null);

    // Fetch latest template data when modal opens
    useEffect(() => {
        const fetchLatestTemplate = async () => {
            if (!open || !teamId || !template?._id) return;

            try {
                const latestTemplate = await getTemplate(teamId, template._id);
                if (latestTemplate?.template) {
                    setCurrentTemplate(latestTemplate.template);
                }
            } catch (error) {
                console.error('Error fetching latest template:', error);
                setCurrentTemplate(template);
            }
        };

        fetchLatestTemplate();
    }, [open, teamId, template?._id]);

    // Fetch team members when modal opens
    useEffect(() => {
        const fetchMembers = async () => {
            if (!open || !teamId) return;

            setLoadingMembers(true);
            try {
                const teamMembersResponse = await getTeamMembers(teamId, { status: 'active' });
                const teamMembers = teamMembersResponse?.members || [];
                setAllTeamMembersList(teamMembers);
                await getMembers(teamId, { status: 'active' });
            } catch (error) {
                console.error('Error fetching members:', error);
            } finally {
                setLoadingMembers(false);
            }
        };

        fetchMembers();
    }, [open, teamId, getMembers]);

    // Fetch subteams when filter button is clicked
    useEffect(() => {
        const fetchSubteams = async () => {
            if (!subteamFilterAnchor || !teamId) return;

            setLoadingSubteams(true);
            try {
                const response = await getTeamSubteams(teamId);
                if (response?.success && response?.subteams) {
                    setSubteams(response.subteams);
                } else if (response?.subteams) {
                    setSubteams(response.subteams);
                }
            } catch (error) {
                console.error('Error fetching subteams:', error);
            } finally {
                setLoadingSubteams(false);
            }
        };

        fetchSubteams();
    }, [subteamFilterAnchor, teamId]);

    const getMemberId = (member) => {
        return member._id || member.userId || member.user?._id || member.user?.userId;
    };

    const getMemberEmail = (member) => {
        return member.user?.email || member.email || '';
    };

    const getMemberName = (member) => {
        return member.user?.fullName || member.fullName || 'Unknown';
    };

    const matchesSearch = (member) => {
        if (!searchQuery) return true;
        const query = searchQuery.toLowerCase();
        const fullName = (member.user?.fullName || member.fullName || '').toLowerCase();
        const email = (member.user?.email || member.email || '').toLowerCase();
        return fullName.includes(query) || email.includes(query);
    };

    // Get template ID for comparison
    const currentTemplateId = useMemo(() => {
        const templateToUse = currentTemplate || template;
        return templateToUse?._id?.toString() || templateToUse?.id?.toString();
    }, [currentTemplate, template]);

    // Build a set of userIds that are assigned to this template
    // Check BOTH: 1) template.assignedMembers array, AND 2) member's card appliedTemplateId
    const assignedUserIds = useMemo(() => {
        const templateToUse = currentTemplate || template;
        const userIds = new Set();

        // Source 1: template.assignedMembers array (populated User objects)
        if (templateToUse?.assignedMembers && Array.isArray(templateToUse.assignedMembers)) {
            templateToUse.assignedMembers.forEach(m => {
                if (!m) return;
                // Populated user object has userId field (Firebase UID)
                if (typeof m === 'object' && m.userId) {
                    userIds.add(m.userId.toString());
                }
                // Could also be just the ObjectId string (unpopulated)
                else if (typeof m === 'string') {
                    userIds.add(m.toString());
                }
            });
        }

        // Source 2: Check each team member's card for appliedTemplateId
        // This catches members assigned via "Choose Template" in Edit Team Member page
        if (currentTemplateId && allTeamMembersList.length > 0) {
            allTeamMembersList.forEach(member => {
                const cardTemplateId = member.defaultCard?.appliedTemplateId?.toString();
                if (cardTemplateId && cardTemplateId === currentTemplateId) {
                    const userId = member.userId || member.user?.userId;
                    if (userId) {
                        userIds.add(userId.toString());
                    }
                }
            });
        }

        return userIds;
    }, [currentTemplate, template, currentTemplateId, allTeamMembersList]);

    const assignedMembers = useMemo(() => {
        return allTeamMembersList.filter(member => {
            const userId = member.userId || member.user?.userId;
            return userId && assignedUserIds.has(userId.toString());
        });
    }, [allTeamMembersList, assignedUserIds]);

    const unassignedMembers = useMemo(() => {
        return allTeamMembersList.filter(member => {
            const userId = member.userId || member.user?.userId;
            if (!userId || assignedUserIds.has(userId.toString())) return false;
            if (!matchesSearch(member)) return false;

            // Filter by subteam if applied
            if (appliedSubteamFilter.size > 0) {
                const memberSubteamId = member.subteamId?.toString();
                if (!memberSubteamId || !appliedSubteamFilter.has(memberSubteamId)) {
                    return false;
                }
            }

            return true;
        });
    }, [allTeamMembersList, assignedUserIds, searchQuery, appliedSubteamFilter]);

    const filteredAssignedMembers = useMemo(() => {
        return assignedMembers.filter(member => {
            if (!matchesSearch(member)) return false;

            // Filter by subteam if applied
            if (appliedSubteamFilter.size > 0) {
                const memberSubteamId = member.subteamId?.toString();
                if (!memberSubteamId || !appliedSubteamFilter.has(memberSubteamId)) {
                    return false;
                }
            }

            return true;
        });
    }, [assignedMembers, searchQuery, appliedSubteamFilter]);

    const handleSubteamFilterClick = (event) => {
        setSubteamFilterAnchor(event.currentTarget);
    };

    const handleSubteamFilterClose = () => {
        setSubteamFilterAnchor(null);
    };

    const handleSubteamToggle = (subteamId) => {
        const newSet = new Set(selectedSubteams);
        if (newSet.has(subteamId)) {
            newSet.delete(subteamId);
        } else {
            newSet.add(subteamId);
        }
        setSelectedSubteams(newSet);
    };

    const handleClearSubteamFilter = () => {
        setSelectedSubteams(new Set());
        setAppliedSubteamFilter(new Set());
    };

    const handleApplySubteamFilter = () => {
        setAppliedSubteamFilter(new Set(selectedSubteams));
        handleSubteamFilterClose();
    };

    // Select All handlers
    const handleSelectAllAssigned = () => {
        if (filteredAssignedMembers.length === 0) return;
        const allSelected = filteredAssignedMembers.every(member => {
            const memberId = getMemberId(member).toString();
            return selectedAssigned.has(memberId);
        });

        if (allSelected) {
            // Deselect all
            setSelectedAssigned(new Set());
        } else {
            // Select all
            const newSet = new Set(selectedAssigned);
            filteredAssignedMembers.forEach(member => {
                newSet.add(getMemberId(member).toString());
            });
            setSelectedAssigned(newSet);
        }
    };

    const handleSelectAllUnassigned = () => {
        if (unassignedMembers.length === 0) return;
        const allSelected = unassignedMembers.every(member => {
            const memberId = getMemberId(member).toString();
            return selectedUnassigned.has(memberId);
        });

        if (allSelected) {
            // Deselect all
            setSelectedUnassigned(new Set());
        } else {
            // Select all
            const newSet = new Set(selectedUnassigned);
            unassignedMembers.forEach(member => {
                newSet.add(getMemberId(member).toString());
            });
            setSelectedUnassigned(newSet);
        }
    };

    const getMemberProfilePicture = (member) => {
        return member.user?.profilePictureUrl || member.profilePictureUrl;
    };

    const handleSave = async () => {
        const templateToUse = currentTemplate || template;
        if (!templateToUse || !onSave) return;

        setIsSubmitting(true);
        try {
            const allMembersForLookup = Array.isArray(allTeamMembersList) ? allTeamMembersList : [];

            console.log('[TeamTemplateAssignMembersModal] handleSave - Debug info:', {
                selectedAssigned: Array.from(selectedAssigned),
                selectedUnassigned: Array.from(selectedUnassigned),
                allMembersCount: allMembersForLookup.length,
                sampleMember: allMembersForLookup[0] ? {
                    _id: allMembersForLookup[0]._id,
                    userId: allMembersForLookup[0].userId,
                    user: allMembersForLookup[0].user ? {
                        _id: allMembersForLookup[0].user._id,
                        userId: allMembersForLookup[0].user.userId
                    } : null
                } : null
            });

            // Get members to assign (from unassigned list that user selected)
            const toAssign = Array.from(selectedUnassigned).map(memberId => {
                const member = allMembersForLookup.find(m => getMemberId(m).toString() === memberId);
                const firebaseUid = member?.userId?.toString() || member?.user?.userId?.toString() || memberId;
                console.log('[TeamTemplateAssignMembersModal] Mapping assign member:', { memberId, foundMember: !!member, firebaseUid });
                return firebaseUid;
            }).filter(Boolean);

            // Get members to unassign (from assigned list that user selected)
            const toUnassign = Array.from(selectedAssigned).map(memberId => {
                const member = allMembersForLookup.find(m => getMemberId(m).toString() === memberId);
                const firebaseUid = member?.userId?.toString() || member?.user?.userId?.toString() || memberId;
                console.log('[TeamTemplateAssignMembersModal] Mapping unassign member:', { memberId, foundMember: !!member, firebaseUid });
                return firebaseUid;
            }).filter(Boolean);

            console.log('[TeamTemplateAssignMembersModal] Final IDs to send:', { toAssign, toUnassign });

            // Also apply the template to each newly assigned member's card
            // This ensures card.appliedTemplateId is set (syncing both systems)
            if (toAssign.length > 0) {
                const applyPromises = toAssign.map(async (memberId) => {
                    try {
                        await applyTemplateToMember(teamId, templateToUse._id, { memberId });
                    } catch (error) {
                        console.warn(`Failed to apply template to member ${memberId}:`, error);
                        // Continue with other members even if one fails
                    }
                });
                await Promise.all(applyPromises);
            }

            // For unassigned members, we need to remove the template from their cards
            // This is handled by the onSave callback which calls assignMembersToTemplate API
            // The backend should handle clearing appliedTemplateId when unassigning

            await onSave({
                templateId: templateToUse._id,
                assignMemberIds: toAssign,
                unassignMemberIds: toUnassign
            });

            setSelectedAssigned(new Set());
            setSelectedUnassigned(new Set());
            onClose();
        } catch (error) {
            console.error('Error saving member assignments:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Modal open={open} onClose={onClose} maxWidth="md" fullWidth>
            <Box ref={modalContentRef} sx={{ padding: 3, paddingTop: 0, paddingRight: 2, display: 'flex', flexDirection: 'column', height: '100%' }}>
                {/* Close button in its own row at top right */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 2, marginTop: 0, flexShrink: 0 }}>
                    <IconButton onClick={onClose} size="small" sx={{ marginTop: '-8px', marginRight: '-8px' }}>
                        <CloseOutlinedIcon />
                    </IconButton>
                </Box>

                {/* Template name, search, and filter button row */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2, gap: 2, flexShrink: 0 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, flex: '0 0 auto' }}>
                        {currentTemplate?.templateName || template?.templateName || 'Template'}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, justifyContent: 'flex-end' }}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                width: '300px',
                                backgroundColor: '#f7f7f7',
                                borderRadius: '20px',
                                border: '1px solid #e3e3e3',
                                padding: '8px 16px',
                                '&:hover': {
                                    boxShadow: 2,
                                },
                            }}
                        >
                            <PersonOutlineOutlinedIcon
                                sx={{
                                    color: '#828282',
                                    fontSize: '18px',
                                    marginRight: '8px'
                                }}
                            />
                            <InputBase
                                fullWidth
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder="Search members..."
                                sx={{
                                    fontSize: '0.7rem',
                                    color: '#4f4f4f',
                                    '& .MuiInputBase-input::placeholder': {
                                        color: '#828282',
                                        opacity: 1,
                                    },
                                }}
                            />
                        </Box>
                        <TeamWhiteButtonWithIcon
                            icon={FilterAltOutlinedIcon}
                            text="Filter by subteam"
                            onClick={handleSubteamFilterClick}
                            padding="0.5rem 1rem"
                            iconProps={{ fontSize: '0.875rem' }}
                            sx={{
                                minWidth: '6rem',
                                border: '0.0625rem solid #e3e3e3',
                            }}
                        />
                        <Menu
                            anchorEl={subteamFilterAnchor}
                            open={Boolean(subteamFilterAnchor)}
                            onClose={handleSubteamFilterClose}
                            disableAutoFocusItem
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'right'
                            }}
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'right'
                            }}
                            PaperProps={{
                                sx: {
                                    borderRadius: '12px',
                                    boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.15)',
                                    width: {
                                        xs: '70vw',
                                        sm: '320px',
                                        md: '350px'
                                    },
                                    maxWidth: '350px',
                                    maxHeight: '60vh',
                                    overflow: 'hidden',
                                    marginTop: '8px',
                                    padding: '12px',
                                    backgroundColor: '#fafafa'
                                }
                            }}
                            MenuListProps={{
                                sx: {
                                    padding: 0
                                }
                            }}
                            slotProps={{
                                backdrop: {
                                    sx: {
                                        cursor: 'default'
                                    }
                                }
                            }}
                        >
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, marginBottom: 2, padding: '0 4px' }}>
                                Subteam
                            </Typography>
                            {loadingSubteams ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', padding: 2 }}>
                                    <CircularProgress size={20} />
                                </Box>
                            ) : subteams.length === 0 ? (
                                <Box sx={{ padding: 2, textAlign: 'center' }}>
                                    <Typography sx={{ fontSize: '0.875rem', color: '#828282' }}>
                                        No subteams found
                                    </Typography>
                                </Box>
                            ) : (
                                <Box sx={{ maxHeight: '60vh', overflowY: 'auto', marginBottom: 2 }}>
                                    {subteams.map((subteam) => (
                                        <MenuItem
                                            key={subteam._id}
                                            onClick={() => handleSubteamToggle(subteam._id.toString())}
                                            sx={{
                                                borderRadius: '8px',
                                                marginBottom: '4px',
                                                padding: '8px 16px',
                                                '&:hover': {
                                                    backgroundColor: '#f0f0f0'
                                                }
                                            }}
                                        >
                                            <Checkbox
                                                checked={selectedSubteams.has(subteam._id.toString())}
                                                sx={{
                                                    color: '#e0e0e0',
                                                    padding: '0',
                                                    marginRight: '12px',
                                                    '&.Mui-checked': {
                                                        color: '#000',
                                                    },
                                                    '& .MuiSvgIcon-root': {
                                                        borderRadius: '0.5rem',
                                                    },
                                                }}
                                            />
                                            <Avatar
                                                src={subteam.logoUrl}
                                                alt={subteam.name}
                                                sx={{
                                                    width: 40,
                                                    height: 40,
                                                    backgroundColor: subteam.logoUrl ? 'transparent' : getSubteamColor(subteam._id, subteam.name),
                                                    border: subteam.logoUrl ? 'none' : 'none',
                                                    flexShrink: 0,
                                                    marginRight: '12px'
                                                }}
                                            >
                                                {!subteam.logoUrl && getSubteamInitials(subteam.name)}
                                            </Avatar>
                                            <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
                                                {subteam.name}
                                            </Typography>
                                        </MenuItem>
                                    ))}
                                </Box>
                            )}
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, marginTop: 2 }}>
                                <TeamWhiteButtonWithIcon
                                    text="Clear filter"
                                    onClick={handleClearSubteamFilter}
                                    padding="6px 16px"
                                    sx={{
                                        flex: 1,
                                        fontSize: '0.75rem',
                                        fontWeight: '500',
                                        color: '#828282',
                                        border: 'none',
                                    }}
                                />
                                <Button
                                    onClick={handleApplySubteamFilter}
                                    variant="contained"
                                    sx={{
                                        backgroundColor: '#000',
                                        color: '#fff',
                                        borderRadius: '20px',
                                        textTransform: 'none',
                                        fontSize: '0.75rem',
                                        fontWeight: '500',
                                        padding: '6px 16px',
                                        flex: 1,
                                        '&:hover': {
                                            backgroundColor: '#333'
                                        }
                                    }}
                                >
                                    Apply filter
                                </Button>
                            </Box>
                        </Menu>
                    </Box>
                </Box>

                {loadingMembers ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', padding: 4, flex: 1 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Box sx={{ display: 'flex', gap: 2, maxHeight: '400px', overflow: 'auto', flex: 1 }}>
                        {/* Assigned Members */}
                        <Box sx={{ flex: 1 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 1, marginTop: 2 }}>
                                <Typography variant="subtitle2">
                                    Assigned to Template ({filteredAssignedMembers.length})
                                </Typography>
                                {filteredAssignedMembers.length > 0 && (
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Checkbox
                                            checked={filteredAssignedMembers.length > 0 && filteredAssignedMembers.every(member => {
                                                const memberId = getMemberId(member).toString();
                                                return selectedAssigned.has(memberId);
                                            })}
                                            onChange={handleSelectAllAssigned}
                                            sx={{
                                                color: '#e0e0e0',
                                                padding: '0',
                                                marginRight: '8px',
                                                '&.Mui-checked': {
                                                    color: '#000',
                                                },
                                                '& .MuiSvgIcon-root': {
                                                    borderRadius: '0.5rem',
                                                },
                                            }}
                                        />
                                        <Typography variant="body2" sx={{ fontSize: '0.75rem', color: '#4f4f4f' }}>
                                            Select All
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                            <Box sx={{ padding: 1, minHeight: '200px' }}>
                                {filteredAssignedMembers.length === 0 ? (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
                                        <Typography variant="body2" sx={{ color: '#000', fontWeight: 600, marginBottom: 0.5 }}>
                                            No Members Assigned
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: '#999', fontSize: '0.875rem' }}>
                                            Try adding some to the template
                                        </Typography>
                                    </Box>
                                ) : (
                                    filteredAssignedMembers.map((member) => {
                                        const memberId = getMemberId(member).toString();
                                        const profilePic = getMemberProfilePicture(member);
                                        return (
                                            <Box
                                                key={memberId}
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    padding: 1.5,
                                                    gap: 1.5,
                                                    '&:hover': { backgroundColor: '#f5f5f5' },
                                                    borderRadius: 1,
                                                    marginBottom: 0.5
                                                }}
                                            >
                                                <Checkbox
                                                    checked={selectedAssigned.has(memberId)}
                                                    onChange={(e) => {
                                                        const newSet = new Set(selectedAssigned);
                                                        if (e.target.checked) {
                                                            newSet.add(memberId);
                                                        } else {
                                                            newSet.delete(memberId);
                                                        }
                                                        setSelectedAssigned(newSet);
                                                    }}
                                                    sx={{
                                                        color: '#e0e0e0',
                                                        padding: '0',
                                                        marginRight: '12px',
                                                        '&.Mui-checked': {
                                                            color: '#000',
                                                        },
                                                        '& .MuiSvgIcon-root': {
                                                            borderRadius: '0.5rem',
                                                        },
                                                    }}
                                                />
                                                <Avatar
                                                    src={profilePic || avatarPlaceholder}
                                                    sx={{
                                                        width: 40,
                                                        height: 40
                                                    }}
                                                />
                                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                                    <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
                                                        {getMemberName(member)}
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ color: '#666', fontSize: '0.75rem' }}>
                                                        {getMemberEmail(member)}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        );
                                    })
                                )}
                            </Box>
                        </Box>

                        {/* Unassigned Members */}
                        <Box sx={{ flex: 1 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 1, marginTop: 2 }}>
                                <Typography variant="subtitle2">
                                    Not Assigned to Template ({unassignedMembers.length})
                                </Typography>
                                {unassignedMembers.length > 0 && (
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Checkbox
                                            checked={unassignedMembers.length > 0 && unassignedMembers.every(member => {
                                                const memberId = getMemberId(member).toString();
                                                return selectedUnassigned.has(memberId);
                                            })}
                                            onChange={handleSelectAllUnassigned}
                                            sx={{
                                                color: '#e0e0e0',
                                                padding: '0',
                                                marginRight: '8px',
                                                '&.Mui-checked': {
                                                    color: '#000',
                                                },
                                                '& .MuiSvgIcon-root': {
                                                    borderRadius: '0.5rem',
                                                },
                                            }}
                                        />
                                        <Typography variant="body2" sx={{ fontSize: '0.75rem', color: '#4f4f4f' }}>
                                            Select All
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                            <Box sx={{ padding: 1, minHeight: '200px' }}>
                                {unassignedMembers.length === 0 ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
                                        <Typography variant="body2" sx={{ color: '#999' }}>
                                            No unassigned members
                                        </Typography>
                                    </Box>
                                ) : (
                                    unassignedMembers.map((member) => {
                                        const memberId = getMemberId(member).toString();
                                        const profilePic = getMemberProfilePicture(member);
                                        return (
                                            <Box
                                                key={memberId}
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    padding: 1.5,
                                                    gap: 1.5,
                                                    '&:hover': { backgroundColor: '#f5f5f5' },
                                                    borderRadius: 1,
                                                    marginBottom: 0.5
                                                }}
                                            >
                                                <Checkbox
                                                    checked={selectedUnassigned.has(memberId)}
                                                    onChange={(e) => {
                                                        const newSet = new Set(selectedUnassigned);
                                                        if (e.target.checked) {
                                                            newSet.add(memberId);
                                                        } else {
                                                            newSet.delete(memberId);
                                                        }
                                                        setSelectedUnassigned(newSet);
                                                    }}
                                                    sx={{
                                                        color: '#e0e0e0',
                                                        padding: '0',
                                                        marginRight: '12px',
                                                        '&.Mui-checked': {
                                                            color: '#000',
                                                        },
                                                        '& .MuiSvgIcon-root': {
                                                            borderRadius: '0.5rem',
                                                        },
                                                    }}
                                                />
                                                <Avatar
                                                    src={profilePic || avatarPlaceholder}
                                                    sx={{
                                                        width: 40,
                                                        height: 40
                                                    }}
                                                />
                                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                                    <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
                                                        {getMemberName(member)}
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ color: '#666', fontSize: '0.75rem' }}>
                                                        {getMemberEmail(member)}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        );
                                    })
                                )}
                            </Box>
                        </Box>
                    </Box>
                )}

                {/* Text and Link on left, Update button on right - same horizontal line */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 2, flexShrink: 0 }}>
                    {/* Left Side: Text and Link */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
                        <Typography
                            sx={{
                                fontSize: '0.875rem',
                                color: '#4f4f4f',
                                marginBottom: 0,
                                lineHeight: 1.2
                            }}
                        >
                            Don't see a member on the list?
                        </Typography>
                        <Link
                            component="button"
                            onClick={(e) => {
                                e.preventDefault();
                                onClose(); // Close the modal first
                                navigate('/team/add-members');
                            }}
                            sx={{
                                fontSize: '0.875rem',
                                color: '#29aef8',
                                textDecoration: 'none',
                                cursor: 'pointer',
                                lineHeight: 1.2,
                                marginTop: 0,
                                padding: 0,
                                border: 'none',
                                background: 'none',
                                textAlign: 'left',
                                '&:hover': {
                                    textDecoration: 'underline'
                                }
                            }}
                        >
                            Add them here
                        </Link>
                    </Box>

{/*                    {/* Right Side: Update button - dynamic text based on selection */}
                    <Button
                        onClick={handleSave}
                        variant="contained"
                        disabled={isSubmitting || (selectedAssigned.size === 0 && selectedUnassigned.size === 0)}
                        sx={{
                            backgroundColor: (selectedAssigned.size === 0 && selectedUnassigned.size === 0) ? '#e0e0e0' : '#000',
                            color: (selectedAssigned.size === 0 && selectedUnassigned.size === 0) ? '#a6a6a6' : '#fff',
                            borderRadius: '20px',
                            textTransform: 'none',
                            fontSize: '0.7rem',
                            fontWeight: '600',
                            padding: '10px 32px',
                            minWidth: '120px',
                            '&:hover': {
                                backgroundColor: (selectedAssigned.size === 0 && selectedUnassigned.size === 0) ? '#e0e0e0' : '#333',
                            },
                            '&.Mui-disabled': {
                                backgroundColor: '#e0e0e0',
                                color: '#a6a6a6'
                            }
                        }}
                    >
                        {isSubmitting ? (
                            <CircularProgress size={20} />
                        ) : selectedAssigned.size > 0 ? (
                            `Remove ${selectedAssigned.size} member${selectedAssigned.size !== 1 ? 's' : ''}`
                        ) : selectedUnassigned.size > 0 ? (
                            `Add ${selectedUnassigned.size} member${selectedUnassigned.size !== 1 ? 's' : ''}`
                        ) : (
                            'Update'
                        )}
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
};

TeamTemplateAssignMembersModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    template: PropTypes.object,
    teamId: PropTypes.string.isRequired,
    onSave: PropTypes.func.isRequired
};

export default TeamTemplateAssignMembersModal;

